import { Tooltip } from '@mui/material';
import React, { FC, RefObject } from 'react';

import translate from '../../../../i18n/translate';

import { AttachmentActionType } from '../../../../models/attachment';

import MuiIcon from '../../../MUI/MuiIcon';

import { MuiIconName, MuiSymbolIconName } from '../../../MUI/MuiIcon/types';

import * as Styled from './styled';

const ICON_NAME: Record<AttachmentActionType, MuiIconName | MuiSymbolIconName> =
  {
    download: 'download',
    preview: 'preview',
  };
const LABEL: Record<AttachmentActionType, string> = {
  download: 'ARIA__LABEL__DOWNLOAD',
  preview: 'FILE__PREVIEW',
};

export type Props = {
  actionType?: AttachmentActionType;
  color?: string;
  colorHover?: string;
  iconSize?: number;
  link?: string;
  linkRef: RefObject<HTMLAnchorElement>;
  isInProgress?: boolean;
  requestButtonClick: () => void;
};

/**
 * Button used for file download/preview/upload action
 *
 * @param props                    Props passed to the component
 * @param props.actionType         Button action type (download/preview)
 * @param props.isInProgress       Whether the download is in progress (WebView only)
 * @param props.link               Presigned url
 * @param props.linkRef            Ref to hidden link
 * @param props.requestButtonClick Request download button click
 * @returns                        The component itself
 */
const ButtonAttachmentAction: FC<Props> = ({
  actionType = 'download',
  isInProgress = false,
  link,
  linkRef,
  requestButtonClick,
}) => {
  const linkTarget = actionType === 'download' ? undefined : '_blank';
  const label = translate(LABEL[actionType]);

  return (
    // Use div as a wrapper because download button is used inside of <Ribbon/>
    <div>
      <Tooltip placement="top" title={label}>
        <Styled.ButtonAction
          aria-label={label}
          loading={isInProgress}
          onClick={requestButtonClick}
        >
          <MuiIcon fontSize="small" icon={ICON_NAME[actionType]} />
        </Styled.ButtonAction>
      </Tooltip>
      <Styled.HiddenLink
        aria-hidden="true"
        download={actionType === 'download'}
        href={link}
        ref={linkRef}
        target={linkTarget}
        title={label}
      />
    </div>
  );
};

export default ButtonAttachmentAction;
